import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
  Chip,
  Button,
  Snackbar,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ExpandMore, Save, CheckCircle, ContentCopy, Sync } from '@mui/icons-material';
import { useGetActiveSynthflowAccountsQuery, useSyncSynthflowAccountsMutation } from '../store/api/synthflowApi';
import { useGetAgentsForAccountQuery, useToggleAgentWebhookMutation, useSyncSynthflowAgentsMutation } from '../store/api/synthflowAgentsApi';
import { useGetLastWebhookDataQuery } from '../store/api/webhooksApi';
import { 
  useGetEmailAddressesQuery, 
  useGetAgentEmailAssignmentsQuery,
  useUpdateAgentEmailAssignmentsMutation,
  EmailAddressDropdown,
  UpdateAgentEmailAssignmentsRequest
} from '../store/api/emailAddressesApi';
import { useSelector } from 'react-redux';

export const CallSummaryPage: React.FC = () => {
  const [selectedAccount, setSelectedAccount] = useState<number | ''>('');
  const [selectedAgent, setSelectedAgent] = useState<number | ''>('');
  
  // Email address management state
  const [toEmails, setToEmails] = useState<EmailAddressDropdown[]>([]);
  const [ccEmails, setCcEmails] = useState<EmailAddressDropdown[]>([]);
  const [bccEmails, setBccEmails] = useState<EmailAddressDropdown[]>([]);
  
  // Save button state management
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Sync feedback state
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [originalAssignments, setOriginalAssignments] = useState<{
    to: EmailAddressDropdown[];
    cc: EmailAddressDropdown[];
    bcc: EmailAddressDropdown[];
  } | null>(null);
  
  // Get current user info for debugging
  const { user } = useSelector((state: any) => state.auth);
  
  const {
    data: accounts = [],
    isLoading,
    error
  } = useGetActiveSynthflowAccountsQuery();

  // Helper function to check if account is valid
  const isAccountValid = selectedAccount !== '' && typeof selectedAccount === 'number';
  
  // Fetch agents when an account is selected
  const {
    data: agents = [],
    isLoading: agentsLoading,
    error: agentsError
  } = useGetAgentsForAccountQuery(selectedAccount as number, {
    skip: !isAccountValid
  });

  // Email address management hooks
  const {
    data: availableEmails = [],
    isLoading: emailsLoading,
    error: emailsError
  } = useGetEmailAddressesQuery();

  
  // Sync mutations
  const [syncAccounts, { isLoading: isSyncingAccounts }] = useSyncSynthflowAccountsMutation();
  const [syncAgents, { isLoading: isSyncingAgents }] = useSyncSynthflowAgentsMutation();

  // Fetch agent email assignments when an agent is selected
  const isAgentValid = selectedAgent !== '' && typeof selectedAgent === 'number';
  const {
    data: agentEmailAssignments,
    isLoading: assignmentsLoading,
    error: assignmentsError
  } = useGetAgentEmailAssignmentsQuery(selectedAgent as number, {
    skip: !isAgentValid
  });

  const [updateAgentEmailAssignments] = useUpdateAgentEmailAssignmentsMutation();
  
  // Webhook management hooks
  const [toggleWebhook, { isLoading: toggleWebhookLoading }] = useToggleAgentWebhookMutation();
  
  // Webhook toggle success/error state
  const [webhookToggleMessage, setWebhookToggleMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [showWebhookDataModal, setShowWebhookDataModal] = useState(false);
  
  // Local state to track webhook enabled status for immediate UI updates
  const [localWebhookEnabled, setLocalWebhookEnabled] = useState<{ [agentId: number]: boolean }>({});
  
  // Helper function to get webhook enabled status (local state takes precedence)
  const getWebhookEnabledStatus = (agentId: number, defaultStatus: boolean): boolean => {
    return localWebhookEnabled[agentId] !== undefined ? localWebhookEnabled[agentId] : defaultStatus;
  };
  
  // Webhook data query
  const { data: lastWebhookData, error: webhookDataError, isLoading: webhookDataLoading } = useGetLastWebhookDataQuery(
    typeof selectedAgent === 'number' ? selectedAgent : 0,
    { skip: typeof selectedAgent !== 'number' || !showWebhookDataModal }
  );


  // Clear selections when accounts change to empty
  useEffect(() => {
    if (accounts.length === 0) {
      setSelectedAccount('');
      setSelectedAgent('');
    }
  }, [accounts]);

  // Load agent email assignments when agent changes
  useEffect(() => {
    if (agentEmailAssignments) {
      setToEmails(agentEmailAssignments.to_emails);
      setCcEmails(agentEmailAssignments.cc_emails);
      setBccEmails(agentEmailAssignments.bcc_emails);
      
      // Store original assignments for change detection
      setOriginalAssignments({
        to: agentEmailAssignments.to_emails,
        cc: agentEmailAssignments.cc_emails,
        bcc: agentEmailAssignments.bcc_emails
      });
    } else {
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setOriginalAssignments({
        to: [],
        cc: [],
        bcc: []
      });
    }
  }, [agentEmailAssignments]);

  const handleAccountChange = (event: any) => {
    setSelectedAccount(event.target.value);
    setSelectedAgent(''); // Reset agent selection when account changes
  };

  const handleAgentChange = (event: any) => {
    setSelectedAgent(event.target.value);
  };

  // Sync handler functions
  const handleSyncAccounts = async () => {
    try {
      const result = await syncAccounts().unwrap();
      setSyncMessage({ type: 'success', text: `Accounts synced successfully: ${result.message}` });
      // Clear any stale account selection
      setSelectedAccount('');
      setSelectedAgent('');
    } catch (error: any) {
      setSyncMessage({ 
        type: 'error', 
        text: `Failed to sync accounts: ${error?.data?.detail || error?.message || 'Unknown error'}` 
      });
    }
  };

  const handleSyncAgents = async () => {
    try {
      const result = await syncAgents().unwrap();
      setSyncMessage({ 
        type: 'success', 
        text: `Agents synced successfully: ${result.total_agents_fetched} fetched, ${result.total_agents_created} created, ${result.total_agents_updated} updated` 
      });
    } catch (error: any) {
      setSyncMessage({ 
        type: 'error', 
        text: `Failed to sync agents: ${error?.data?.detail || error?.message || 'Unknown error'}` 
      });
    }
  };


  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!originalAssignments) return false;
    
    const toChanged = JSON.stringify(toEmails.map(e => e.id).sort()) !== 
                     JSON.stringify(originalAssignments.to.map(e => e.id).sort());
    const ccChanged = JSON.stringify(ccEmails.map(e => e.id).sort()) !== 
                     JSON.stringify(originalAssignments.cc.map(e => e.id).sort());
    const bccChanged = JSON.stringify(bccEmails.map(e => e.id).sort()) !== 
                      JSON.stringify(originalAssignments.bcc.map(e => e.id).sort());
    
    return toChanged || ccChanged || bccChanged;
  };

  const handleSaveEmailAssignments = async () => {
    if (!isAgentValid) return;
    
    setIsSaving(true);
    try {
      const assignmentsData: UpdateAgentEmailAssignmentsRequest = {
        to_emails: toEmails.map(email => email.id),
        cc_emails: ccEmails.map(email => email.id),
        bcc_emails: bccEmails.map(email => email.id)
      };
      
      await updateAgentEmailAssignments({
        agentId: selectedAgent as number,
        data: assignmentsData
      }).unwrap();
      
      // Update original assignments to reflect saved state
      setOriginalAssignments({
        to: toEmails,
        cc: ccEmails,
        bcc: bccEmails
      });
      
      // Show success message
      setShowSuccessMessage(true);
      
    } catch (error) {
      console.error('Failed to save email assignments:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Call Summary
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Failed to load Synthflow accounts
          </Typography>
          <Typography variant="body2" gutterBottom>
            Error details: {
              (error as any)?.data?.detail || 
              (error as any)?.message || 
              'Unknown error'
            }
          </Typography>
          <Typography variant="body2">
            Please check your Settings page to ensure your Umbrella User ID is configured correctly.
          </Typography>
        </Alert>
        
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Call Summary
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        View call summaries for your active Aigent product subscriptions
      </Typography>


      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Aigent Account
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="account-select-label">Aigent Account</InputLabel>
                  <Select
                    labelId="account-select-label"
                    id="account-select"
                    value={selectedAccount}
                    label="Aigent Account"
                    onChange={handleAccountChange}
                  >
                    {accounts.length === 0 ? (
                      <MenuItem disabled>
                        No active Aigent accounts found
                      </MenuItem>
                    ) : (
                      accounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<Sync />}
                  onClick={handleSyncAccounts}
                  disabled={isSyncingAccounts}
                  sx={{ minWidth: 'auto', px: 2 }}
                  title="Sync accounts from Synthflow platform"
                >
                  {isSyncingAccounts ? <CircularProgress size={20} /> : 'Sync'}
                </Button>
              </Box>
              
              {/* Agent Selection - Only show when account is selected */}
              {selectedAccount && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel id="agent-select-label">Aigent Agent</InputLabel>
                    <Select
                      labelId="agent-select-label"
                      id="agent-select"
                      value={selectedAgent}
                      label="Aigent Agent"
                      onChange={handleAgentChange}
                      disabled={agentsLoading}
                    >
                      {agentsLoading ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading agents...
                        </MenuItem>
                      ) : agentsError ? (
                        <MenuItem disabled>
                          Error loading agents
                        </MenuItem>
                      ) : agents.length === 0 ? (
                        <MenuItem disabled>
                          No agents found for this account
                        </MenuItem>
                      ) : (
                        agents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {agent.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {agent.type && `${agent.type}`}
                                {agent.type && agent.phone_number && ' • '}
                                {agent.phone_number && `${agent.phone_number}`}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<Sync />}
                    onClick={handleSyncAgents}
                    disabled={isSyncingAgents}
                    sx={{ minWidth: 'auto', px: 2 }}
                    title="Sync agents from Synthflow platform"
                  >
                    {isSyncingAgents ? <CircularProgress size={20} /> : 'Sync'}
                  </Button>
                </Box>
              )}
              
              {/* Webhook URL Display - Only show when agent is selected */}
              {selectedAgent && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Webhook URL
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Use this webhook URL in your Synthflow agent configuration to receive call completion notifications:
                  </Typography>
                  {(() => {
                    const selectedAgentData = agents.find(agent => agent.id === selectedAgent);
                    return selectedAgentData?.webhook_url ? (
                      <Box>
                        <TextField
                          fullWidth
                          value={selectedAgentData.webhook_url}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => selectedAgentData.webhook_url && navigator.clipboard.writeText(selectedAgentData.webhook_url)}
                                  edge="end"
                                  size="small"
                                >
                                  <ContentCopy />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            '& .MuiInputBase-input': { 
                              fontFamily: 'monospace',
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Click the copy icon to copy the webhook URL to clipboard
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="warning">
                        Webhook URL not available for this agent. Please contact support if this issue persists.
                      </Alert>
                    );
                  })()}
                </Box>
              )}
              
              {/* Webhook Management - Only show when agent is selected */}
              {selectedAgent && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Webhook Management
                  </Typography>
                  {(() => {
                    const selectedAgentData = agents.find(agent => agent.id === selectedAgent);
                    return selectedAgentData ? (() => {
                      // Use local state for webhook enabled status
                      const webhookEnabled = getWebhookEnabledStatus(selectedAgentData.id, selectedAgentData.webhook_enabled);
                      
                      return (
                        <Box>
                          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Webhook listening is currently:
                            </Typography>
                            <Chip 
                              label={webhookEnabled ? "Enabled" : "Disabled"}
                              color={webhookEnabled ? "success" : "default"}
                              size="small"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {webhookEnabled 
                              ? "This agent will receive call completion notifications from Synthflow."
                              : "This agent will ignore call completion notifications from Synthflow."
                            }
                          </Typography>
                          <Button
                            variant="outlined"
                            color={webhookEnabled ? "error" : "success"}
                            onClick={async () => {
                              if (selectedAgentData) {
                                try {
                                  setWebhookToggleMessage(null);
                                  
                                  // Enhanced logging for debugging
                                  console.log('🔄 Starting webhook toggle:', {
                                    agentId: selectedAgentData.id,
                                    agentName: selectedAgentData.name,
                                    currentEnabled: webhookEnabled,
                                    newEnabled: !webhookEnabled
                                  });
                                  
                                  const result = await toggleWebhook({ 
                                    agentId: selectedAgentData.id, 
                                    enabled: !webhookEnabled 
                                  }).unwrap();
                                
                                console.log('✅ Webhook toggle successful:', result);
                                
                                // Update local state for immediate UI updates
                                setLocalWebhookEnabled(prev => ({
                                  ...prev,
                                  [selectedAgentData.id]: result.webhook_enabled
                                }));
                                
                                setWebhookToggleMessage({
                                  type: 'success',
                                  message: result.message || `Webhook ${result.webhook_enabled ? 'enabled' : 'disabled'} successfully!`
                                });
                                
                                // Clear message after 5 seconds (as requested)
                                setTimeout(() => setWebhookToggleMessage(null), 5000);
                              } catch (error) {
                                // Enhanced error logging for debugging
                                const errorDetails = error as any;
                                console.error('❌ Webhook toggle error details:', {
                                  error,
                                  errorType: typeof error,
                                  errorMessage: errorDetails?.message,
                                  errorStatus: errorDetails?.status,
                                  errorData: errorDetails?.data,
                                  agentId: selectedAgentData.id,
                                  agentName: selectedAgentData.name
                                });
                                
                                // More specific error message based on error type
                                let errorMessage = 'Failed to toggle webhook. Please try again.';
                                if (errorDetails?.status === 401) {
                                  errorMessage = 'Authentication failed. Please log in again.';
                                } else if (errorDetails?.status === 404) {
                                  errorMessage = 'Agent not found. Please refresh the page.';
                                } else if (errorDetails?.status === 500) {
                                  errorMessage = 'Server error. Please try again later.';
                                } else if (errorDetails?.status === 'FETCH_ERROR') {
                                  errorMessage = 'Network error. Please check your connection.';
                                }
                                
                                setWebhookToggleMessage({
                                  type: 'error',
                                  message: errorMessage
                                });
                                
                                // Clear message after 5 seconds
                                setTimeout(() => setWebhookToggleMessage(null), 5000);
                              }
                            }
                          }}
                          disabled={toggleWebhookLoading}
                        >
                          {toggleWebhookLoading ? (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          ) : null}
                          {toggleWebhookLoading 
                            ? (webhookEnabled ? "Disabling..." : "Enabling...")
                            : (webhookEnabled ? "Disable Webhook" : "Enable Webhook")
                          }
                        </Button>
                        
                        {/* Last Webhook Data Link */}
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => {
                            setShowWebhookDataModal(true);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Last webhook data
                        </Button>
                      </Box>
                      );
                    })() : null;
                  })()}
                </Box>
              )}
              
              {/* Error handling for agents */}
              {selectedAccount && agentsError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Failed to load agents for the selected account. This could be due to an invalid API key or network issues. Please try again.
                </Alert>
              )}
              
              {/* Info message when no agents found */}
              {selectedAccount && !agentsLoading && !agentsError && agents.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No agents found for this account. This could be because:
                  <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                    <li>The account has no agents configured</li>
                    <li>The API key is invalid or expired</li>
                    <li>The agents are not yet synchronized</li>
                  </ul>
                </Alert>
              )}
              
              {accounts.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No active Aigent product subscriptions found. This could be because:
                  <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                    <li>You don't have an Umbrella User ID configured in your profile</li>
                    <li>You don't have any active Synthflow subscriptions in your Umbrella account</li>
                    <li>Your subscriptions are not in "active" state</li>
                  </ul>
                  Please check your Settings page to ensure your Umbrella User ID is configured.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Info Summary
              </Typography>
              {selectedAccount && selectedAgent ? (
                <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1">
                    Selected Account: {accounts.find(acc => acc.id === selectedAccount)?.name}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Selected Agent: {agents.find(agent => agent.id === selectedAgent)?.name}
                  </Typography>
                  {agents.find(agent => agent.id === selectedAgent)?.phone_number && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Phone: {agents.find(agent => agent.id === selectedAgent)?.phone_number}
                    </Typography>
                  )}
                </Paper>
              ) : selectedAccount ? (
                <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1">
                    Selected Account: {accounts.find(acc => acc.id === selectedAccount)?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please select an agent to view call summaries.
                  </Typography>
                </Paper>
              ) : (
                <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary">
                    Please select an Aigent account to view call summaries.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Email Address Management Section - Only show when agent is selected */}
      {selectedAgent && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Address Management
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Assign email addresses for call summary notifications for the selected agent. 
                  To add new email addresses, go to Settings → Address Book.
                </Typography>


                {/* Email Assignment Section */}
                <Grid container spacing={3}>
                  {/* TO Emails */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      TO Recipients
                    </Typography>
                    <Autocomplete
                      multiple
                      options={availableEmails}
                      value={toEmails}
                      onChange={(event, newValue) => setToEmails(newValue)}
                      getOptionLabel={(option) => option.label ? `${option.label} (${option.email})` : option.email}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.label ? `${option.label} (${option.email})` : option.email}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select TO recipients"
                          size="small"
                        />
                      )}
                      loading={emailsLoading}
                      disabled={emailsLoading}
                    />
                  </Grid>

                  {/* CC Emails */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      CC Recipients
                    </Typography>
                    <Autocomplete
                      multiple
                      options={availableEmails}
                      value={ccEmails}
                      onChange={(event, newValue) => setCcEmails(newValue)}
                      getOptionLabel={(option) => option.label ? `${option.label} (${option.email})` : option.email}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.label ? `${option.label} (${option.email})` : option.email}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select CC recipients"
                          size="small"
                        />
                      )}
                      loading={emailsLoading}
                      disabled={emailsLoading}
                    />
                  </Grid>

                  {/* BCC Emails */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      BCC Recipients
                    </Typography>
                    <Autocomplete
                      multiple
                      options={availableEmails}
                      value={bccEmails}
                      onChange={(event, newValue) => setBccEmails(newValue)}
                      getOptionLabel={(option) => option.label ? `${option.label} (${option.email})` : option.email}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.label ? `${option.label} (${option.email})` : option.email}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select BCC recipients"
                          size="small"
                        />
                      )}
                      loading={emailsLoading}
                      disabled={emailsLoading}
                    />
                  </Grid>
                </Grid>

                {/* Save Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={20} /> : <Save />}
                    onClick={handleSaveEmailAssignments}
                    disabled={isSaving || !hasUnsavedChanges()}
                  >
                    {isSaving ? 'Saving...' : 'Save Email Assignments'}
                  </Button>
                </Box>

                {/* Error Messages */}
                {emailsError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to load email addresses. Please try again.
                  </Alert>
                )}
                
                {assignmentsError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to load email assignments. Please try again.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Success Message */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSuccessMessage(false)} 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ width: '100%' }}
        >
          Email assignments saved successfully!
        </Alert>
      </Snackbar>

      {/* Webhook Toggle Success/Error Snackbar */}
      <Snackbar
        open={!!webhookToggleMessage}
        autoHideDuration={webhookToggleMessage?.type === 'success' ? 3000 : 5000}
        onClose={() => setWebhookToggleMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setWebhookToggleMessage(null)} 
          severity={webhookToggleMessage?.type || 'info'} 
          sx={{ width: '100%' }}
        >
          {webhookToggleMessage?.message}
        </Alert>
      </Snackbar>

      {/* Sync Feedback Snackbar */}
      <Snackbar
        open={!!syncMessage}
        autoHideDuration={syncMessage?.type === 'success' ? 4000 : 6000}
        onClose={() => setSyncMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSyncMessage(null)} 
          severity={syncMessage?.type || 'info'} 
          sx={{ width: '100%' }}
        >
          {syncMessage?.text}
        </Alert>
      </Snackbar>

      {/* Webhook Data Modal */}
      <Dialog
        open={showWebhookDataModal}
        onClose={() => setShowWebhookDataModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Last Webhook Data
          {(() => {
            const selectedAgentData = agents.find(agent => agent.id === selectedAgent);
            return selectedAgentData ? (
              <Typography variant="body2" color="text.secondary">
                Agent: {selectedAgentData.name}
              </Typography>
            ) : null;
          })()}
        </DialogTitle>
        <DialogContent>
          {webhookDataLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : webhookDataError ? (
            <Typography variant="body1" color="error">
              Error loading webhook data. Please try again.
            </Typography>
          ) : lastWebhookData ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Timestamp: {new Date(lastWebhookData.timestamp).toLocaleString()}
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '400px',
                  maxWidth: '100%',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  border: '1px solid #e0e0e0',
                }}
              >
                {JSON.stringify(lastWebhookData.payload, null, 2)}
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No webhook data available for this agent yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWebhookDataModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
